import { IDropdownOption } from "@fluentui/react";
import React, { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useTheme } from "react-jss";
import { DefaultValues } from "../../../../../../../common/enum/DefaultValues";
import { MasterTurnsFormStyle } from "./formStyle.jss";
import { IGenericPayload } from "../../../../../../../models";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux/hooks";
import { getGenericPayloadFromEvent, compileNameOfProperty, mapIDescriptionToDropDownOptions } from "../../../../../../../utils";
import { FormField } from "../../../../../../common/fields/fields";
import { BooleanSelector } from "../../../../../../request/common/selectors";
import { NotDataWarningMessage } from "../../../../notDataWarning/notDataWarning";
import { Selector } from "../../../../../../common/selectors/selector";
import { PopUpCodes } from "../../../../../../../common/enum/PopUpCodes";
import { IMasterTurnRemaining, IMasterTurn } from "../../../../../../../models/resources/master/ITurn";
import { updateMasterTurnsProperty, setMasterTurnsForm } from "../../../../../../../redux/reducers/resources/master/turns/form";




interface IMasterOrganismFormProps {
    showNotData: boolean;
    data: IMasterTurnRemaining[];
}

export const MasterTurnsForm: React.FC<IMasterOrganismFormProps> = ({ showNotData, data }) => {
    const theme = useTheme();
    const style = MasterTurnsFormStyle({ theme });
    const dispatch = useAppDispatch();

    const { t } = useTranslation("common", { keyPrefix: "MASTERS" });

    const { form } = useAppSelector((state) => state.masterTurnsForm);
    const { showPopUp } = useAppSelector((state) => state.masterPopUp);
    const { register, control } = useForm<IMasterTurn>({ mode: "onChange" });

    const onUpdateFormProperty = (name: string, value: any) => {
        const payload: IGenericPayload = {
            name,
            value,
        };

        dispatch(updateMasterTurnsProperty(payload));
    };

    const onInputChange = useCallback(
        (event: any) => {
            const { name, value } = getGenericPayloadFromEvent(event);
            onUpdateFormProperty(name, value);
        },
        [dispatch],
    );
    const onYesNoOptionsChange = (option: IDropdownOption, propertyName: string) => {
        onUpdateFormProperty(propertyName, !!+option.key);
    };
    const onSelectorChange = useCallback(
        (event: any, option: any, name: string) => {
            onUpdateFormProperty(name, option.key);

            let turn = data.find((item) => {
                return item.id === option.key;
            });
            if (turn) {
                const form: IMasterTurn = {
                    active: true,
                    referenceId: String(option.key),
                    code: turn.code,
                    description: turn.description,
                };

                dispatch(setMasterTurnsForm(form));
            }
        },
        [dispatch, data],
    );
    const isDisabledInput = useMemo(() => {
        return showPopUp === PopUpCodes.CREATE && !form.referenceId;
    }, [showPopUp, form.referenceId]);
    return showNotData && showPopUp === PopUpCodes.CREATE ? (
        <NotDataWarningMessage></NotDataWarningMessage>
    ) : (
        <div className={style.form}>
            {!form.id && (
                <div className={style.rowForm}>
                    <Selector
                        selectedKey={Number(form.referenceId)}
                        name={compileNameOfProperty<IMasterTurn>("referenceId")}
                        title={t("FORM.TURN_SELECTOR")}
                        options={mapIDescriptionToDropDownOptions(data)}
                        onChange={onSelectorChange}
                        required={true}
                    />
                </div>
            )}

            <div className={style.rowForm}>
                <FormField
                    type="text"
                    label={t("CODE")}
                    value={form.code ?? DefaultValues.NOT_TEXT}
                    disabled={true}
                    {...register(compileNameOfProperty<IMasterTurn>("code"), {
                        onChange: onInputChange,
                        value: form.code ?? DefaultValues.NOT_TEXT,
                    })}
                    isRequired={true}
                />

                <FormField
                    type="text"
                    label={t("DESCRIPTION")}
                    value={form.description ?? DefaultValues.NOT_TEXT}
                    {...register(compileNameOfProperty<IMasterTurn>("description"), {
                        onChange: onInputChange,
                        value: form.description ?? DefaultValues.NOT_TEXT,
                    })}
                    isRequired={true}
                    disabled={isDisabledInput}
                />
            </div>
            <div className={style.rowForm}>
                <BooleanSelector
                    onChange={onYesNoOptionsChange}
                    selectedKey={+form.active}
                    propertyName={compileNameOfProperty<IMasterTurn>("active")}
                    title={t("ACTIVE")}
                    control={control}
                    isRequired={true}
                    disabled={isDisabledInput}
                />
            </div>
        </div>
    );
};
